import { BookkeepingItem } from '@features/reconciliation/types/bookkeepingItem'
import { getItemAmountInAccountCurrency } from '@features/reconciliation/utils/getItemAmountInAccountCurrency'
import { BankLine } from '@modules-deprecated/bankReconciliation/types'

interface GetTopMatchesParams {
  transaction: BankLine
  items: BookkeepingItem[]
  accountCurrency?: string
  count: number
}

const scoreItems = (items: BookkeepingItem[], transaction: BankLine, accountCurrency?: string) => {
  const transactionAmount = transaction.amount
  const transactionDate = new Date(transaction.entryDate)

  return items.map((item) => {
    // Convert item amount to account currency if needed
    const itemAmountInAccountCurrency = getItemAmountInAccountCurrency(item, accountCurrency)

    const amountDiff = Math.abs(itemAmountInAccountCurrency - transactionAmount)
    const daysDiff = Math.abs(
      Math.floor((new Date(item.date).getTime() - transactionDate.getTime()) / (1000 * 60 * 60 * 24)),
    )

    return { item, amountDiff, daysDiff }
  })
}

const sortByScore = <T extends { amountDiff: number; daysDiff: number }>(scoredItems: T[]) =>
  scoredItems.sort((a, b) => {
    // Sort by amount difference
    const amountComparison = a.amountDiff - b.amountDiff
    if (amountComparison !== 0) {
      return amountComparison
    }

    // If amounts are equal, sort by date difference
    return a.daysDiff - b.daysDiff
  })

export const getTopMatches = ({
  transaction,
  items,
  accountCurrency,
  count,
}: GetTopMatchesParams): BookkeepingItem[] => {
  // Filter items by matching side
  const matchingSideItems = items.filter((item) => item.side === transaction.side)

  // If no items match the transaction's side, fall back to scoring all items
  const candidateItems = matchingSideItems.length > 0 ? matchingSideItems : items
  const scoredItems = scoreItems(candidateItems, transaction, accountCurrency)

  return sortByScore(scoredItems)
    .slice(0, count)
    .map((scored) => scored.item)
}
